import { useAtom } from "jotai";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import "../css/LanguageTranslation.css";
import { clinicAtom } from "../../_helper/constant/constant-atom";
import { useEffect, useState } from "react";
import InputField from "../../common_component/component/InputField";
import CuDropdown from "../../common_component/component/CuDropdown";
import {
  useCreateItem,
  useSecureUpdateItem,
} from "../../_helper/actions/mutation/items";
import CuSpinner from "../../common_component/component/CuSpinner";
import { toast } from "react-toastify";

const keyOptions = [
  { key: 1, value: "getNotes", text: "NOTES", API: ApiEndPoint.QP_CM_LANGUAGE },
  {
    key: 2,
    value: "getFrequency",
    text: "FREQUENCY",
    API: ApiEndPoint.QP_CM_LANGUAGE,
  },
  { key: 3, value: "getWhen", text: "WHEN", API: ApiEndPoint.QP_CM_LANGUAGE },
  {
    key: 4,
    value: "getAdvices",
    text: "ADVICE",
    API: ApiEndPoint.CONSULTATION,
  },
  { key: 5, value: "others", text: "OTHERS", API: ApiEndPoint.CONSULTATION },
];

const LanguageTranslation = ({ editData, mode, handleEdit, handleCancel }) => {
  const [clinic] = useAtom(clinicAtom);
  const [formData, setFormData] = useState({ english: "" });
  const [selectedKey, setSelectedKey] = useState("getNotes");
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const { data: languages } = useItemsFilter(
    ApiEndPoint.QP_CM_LANGUAGE,
    `?action=getLanguages&clinic_id=${clinic?.id}&backend=True`,
    Boolean(!!clinic?.id)
  );

  // Dynamically select the API endpoint based on the selected key
  const selectedKeyOption = keyOptions.find(
    (option) => option.value === selectedKey
  );

  const { data: suggestions } = useItemsFilter(
    selectedKeyOption?.API,
    `?action=${selectedKey}&clinic_id=${clinic?.id}&name=${searchInput}`,
    clinic?.id && searchInput?.length > 0 ? true : false
  );

  useEffect(() => {
    if (editData && languages) {
      setFormData(() => {
        let data = { english: editData?.name };
        for (let i of editData?.CustomTranslationValues) {
          data[i?.language?.name] = {
            id: i?.language?.id,
            value: i?.value,
            valId: i?.id,
          };
        }
        for (let i of languages) {
          if (!data[i?.name]) {
            data[i?.name] = { id: i?.id, value: "" };
          }
        }
        return data;
      });
    }
  }, [editData, languages]);

  useEffect(() => {
    if (suggestions) {
      const options = suggestions.map((itm) => ({
        key: itm?.id,
        value: itm?.name,
        text: itm?.name,
      }));
      setSuggestionOptions(options);
    }
  }, [suggestions]);

  useEffect(() => {
    if (languages && mode !== "edit") {
      const keys = {};
      for (let i of languages) {
        keys[i?.name] = { id: i?.id, value: "" };
      }
      setFormData((prev) => ({ ...prev, ...keys }));
    }
  }, [languages, mode]);

  const handleKeyChange = (val) => {
    setSelectedKey(val);
  };

  const handleSelectSuggestion = (val) => {
    setFormData((prev) => ({ ...prev, english: val }));
  };

  const handleChange = (val, label) => {
    if (label === "english") {
      setFormData((prev) => ({
        ...prev,
        [label]: val,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [label]: { ...prev[label], value: val },
      }));
    }
  };

  const postTranslationMutation = useCreateItem(ApiEndPoint.TRANSLATION);
  const updateTranslationMutation = useSecureUpdateItem(
    ApiEndPoint.TRANSLATION
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const customTranslationValues = Object.keys(formData)
      .filter((itm) => itm !== "english" && formData[itm]?.value?.length > 0)
      .map((key) => ({
        id: formData[key]?.valId,
        language: formData[key]?.id,
        status: "draft",
        value: formData[key]?.value,
      }));

    if (customTranslationValues.length > 0) {
      const payload = {
        id: editData?.id,
        action:
          mode === "edit" ? "patchCustomTranslation" : "postCustomTranslation",
        clinic_id: clinic?.id,
        backend: "True",
        custom_translation_values: customTranslationValues,
        status: "draft",
        name: formData.english,
      };

      if (mode === "edit") {
        updateTranslationMutation.mutate(
          { id: null, data: payload },
          {
            onSuccess: () => {
              toast.success("Updated Successfully");
              handleCancel();
            },
            onError: () => {
              toast.error("Something went wrong, please try again");
            },
          }
        );
      } else {
        postTranslationMutation.mutate(payload, {
          onSuccess: () => {
            toast.success("Created Successfully");
            handleCancel();
          },
          onError: () => {
            toast.error("Something went wrong, please try again");
          },
        });
      }
    } else {
      toast.error("Please add at least one translation");
    }
  };

  return (
    <div className="cu-translation-main-container">
      <form className="cu-translation-create-form" onSubmit={handleSubmit}>
        <div className="cu-translation-create-form-input">
          <label>Select Key</label>
          <CuDropdown
            options={keyOptions}
            disabled={mode === "view"}
            value={selectedKey}
            onChange={handleKeyChange}
          />
        </div>
        {selectedKey !== "others" && (
          <div className="cu-translation-create-form-input">
            <label>Text Suggestions</label>
            <CuDropdown
              options={suggestionOptions}
              disabled={mode === "view"}
              onChange={handleSelectSuggestion}
              searchable
              onSearch={(val) => setSearchInput(val?.toUpperCase())}
            />
          </div>
        )}
        <div className="cu-translation-create-form-input">
          <InputField
            type="text"
            label="English"
            disabled={mode === "view" || selectedKey !== "others"}
            value={formData.english}
            onChange={(e) =>
              handleChange(e.target.value?.toUpperCase(), "english")
            }
          />
        </div>
        {Object.keys(formData).map((lang) =>
          lang !== "english" ? (
            <div className="cu-translation-create-form-input" key={lang}>
              <InputField
                type="text"
                label={lang}
                disabled={mode === "view"}
                value={formData[lang]?.value}
                onChange={(e) => handleChange(e.target.value, lang)}
              />
            </div>
          ) : null
        )}
        <div className="cu-translation-create-form-btn-container">
          {mode !== "view" && (
            <button
              className="cu-translation-create-form-btn cu-translation-create-form-save-btn"
              type="submit"
            >
              {postTranslationMutation.isLoading ? <CuSpinner /> : "Save"}
            </button>
          )}
          {mode === "view" && (
            <button
              className="cu-translation-create-form-btn cu-translation-create-form-save-btn"
              type="submit"
              onClick={handleEdit}
            >
              Edit
            </button>
          )}
          <button
            onClick={handleSubmit}
            className="cu-translation-create-form-btn cu-translation-create-form-save-btn"
            type="button"
          >
            Save
          </button>
          <button
            className="cu-translation-create-form-btn cu-translation-create-form-cancel-btn"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default LanguageTranslation;
